// "Kitchen" and "Bar" in the top bar of a restaurant: how many tickets each station is
// still making, one click from its tickets. Staff call them KOT and BOT, which the
// tooltip says.
import { cx, IconTile } from '@ims/ui'
import { ChefHat, Coffee, type IconType } from '@ims/ui/icons'
import { Link } from 'react-router'
import { useLive, useSettings } from '../hooks.ts'
import { useCan } from '../workspace.tsx'

const STATION = {
  kitchen: { label: 'Kitchen', short: 'KOT', icon: ChefHat, area: 'kitchen', count: 'bg-kitchen' },
  bar: { label: 'Bar', short: 'BOT', icon: Coffee, area: 'take', count: 'bg-take' },
} as const satisfies Record<string, { icon: IconType; [key: string]: unknown }>

function QueuePill({ station, count }: { station: keyof typeof STATION; count: number }) {
  const { label, short, icon: Icon, area, count: countClass } = STATION[station]
  const making = count === 0 ? 'nothing being made' : `${count.toLocaleString('en-IN')} being made`
  return (
    <Link
      to={`/kitchen?station=${station}`}
      title={`${label} tickets (${short}) · ${making}`}
      aria-label={`${label} tickets, ${making}`}
      className={cx(
        'flex h-9 items-center gap-2 rounded-full border border-border bg-surface py-0 pl-1 text-sm font-semibold transition-colors hover:border-primary/40',
        count > 0 ? 'pr-2 text-ink' : 'pr-3.5 text-muted',
      )}
    >
      <IconTile area={area} size="sm" className="rounded-full">
        <Icon className="size-4" />
      </IconTile>
      {label}
      {count > 0 && (
        <span
          className={cx(
            'min-w-5 rounded-full px-1.5 py-px text-center text-xs font-bold text-on-fill tabular-nums',
            countClass,
          )}
        >
          {count.toLocaleString('en-IN')}
        </span>
      )}
    </Link>
  )
}

/** Desktop only: a phone's top bar is full, and Orders → Kitchen is one tap away there. */
export function KitchenQueue() {
  const restaurant = useSettings()?.restaurant ?? false
  const on = useCan('waiter') && restaurant
  // One counting statement: this runs on every screen.
  const queue = useLive(
    (r) => (on ? r.orders.queue() : Promise.resolve(null)),
    [on],
    ['kot_tickets', 'products'],
  ).data
  if (!on || !queue) return null
  return (
    <div className="hidden items-center gap-2 lg:flex">
      <span className="h-5 w-px bg-border" />
      <QueuePill station="kitchen" count={queue.kitchen} />
      {(queue.hasBar || queue.bar > 0) && <QueuePill station="bar" count={queue.bar} />}
    </div>
  )
}
